import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Typography, Chip, IconButton, Tooltip, Stack, alpha } from '@mui/material';
import Iconify from '../../../components/Iconify';

// ─── Date helpers ──────────────────────────────────────────────────────────────
const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    const cleaned = dateStr.replace(/\.\d+/, '');
    const parsed = moment.utc(cleaned, 'DD-MM-YYYY HH:mm:ssZ');
    if (parsed.isValid()) return parsed.local();
    const iso = moment(dateStr);
    return iso.isValid() ? iso : null;
};

const formatDate = (dateStr) => {
    const m = parseApiDate(dateStr);
    return m ? m.format('DD MMM YYYY, h:mm A') : '—';
};

// ─── Form type badge ───────────────────────────────────────────────────────────
const FORM_TYPE_COLORS = {
    MEDICAL_HISTORY:       '#855CF8',
    COMMUNICATION_CONSENT: '#078DEE',
    PHOTOGRAPHY_CONSENT:   '#FF6B6B',
    RADIOGRAPHY_CONSENT:   '#FF8C00',
    SURVEY:                '#26C6DA',
    TREATMENT_PLAN:        '#00AB55',
    CONSENT:               '#637381',
};

export default function FormSubmissionCard({ submission, onView, onDownloadPdf }) {
    const formType = submission.form?.form_type || 'CONSENT';
    const typeColor = FORM_TYPE_COLORS[formType] || '#637381';
    const formName = submission.form?.name || 'Consent Form';

    // Signer info — may be on submission directly or nested
    const signerName = submission.submitted_by_name
        || (submission.lead ? `${submission.lead.first_name || ''} ${submission.lead.last_name || ''}`.trim() : '')
        || 'Patient';

    const submittedAt = submission.submitted_at || submission.created_at;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: '10px',
                border: '1px solid rgba(0,171,85,0.2)',
                bgcolor: 'rgba(0,171,85,0.03)',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,171,85,0.12)' },
            }}
        >
            {/* Form type icon */}
            <Box sx={{
                width: 36, height: 36, borderRadius: '8px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(typeColor, 0.1),
            }}>
                <Iconify icon="solar:document-text-bold-duotone" width={20} sx={{ color: typeColor }} />
            </Box>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formName}
                    </Typography>
                    <Chip
                        label={formType.replace(/_/g, ' ')}
                        size="small"
                        sx={{ fontSize: '10px', height: 18, px: 0.5, bgcolor: alpha(typeColor, 0.08), color: typeColor, fontWeight: 600, border: `1px solid ${alpha(typeColor, 0.2)}` }}
                    />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                        Submitted {formatDate(submittedAt)}
                    </Typography>
                    {signerName && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Iconify icon="eva:person-fill" width={11} sx={{ color: '#00AB55' }} />
                            <Typography variant="caption" sx={{ color: '#00AB55', fontSize: '11px', fontWeight: 600 }}>
                                Signed by {signerName}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Box>

            {/* Submitted badge */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.4, borderRadius: '6px',
                bgcolor: 'rgba(0,171,85,0.12)',
            }}>
                <Iconify icon="eva:checkmark-circle-2-fill" width={13} sx={{ color: '#00AB55' }} />
                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 700, color: '#00AB55' }}>
                    Submitted
                </Typography>
            </Box>

            {/* Actions */}
            <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
                {onView && (
                    <Tooltip title="View submission">
                        <IconButton size="small" onClick={() => onView(submission)} sx={{ color: '#078DEE' }}>
                            <Iconify icon="eva:eye-fill" width={16} />
                        </IconButton>
                    </Tooltip>
                )}
                {onDownloadPdf && (
                    <Tooltip title="Download PDF">
                        <IconButton size="small" onClick={() => onDownloadPdf(submission)} sx={{ color: 'text.secondary' }}>
                            <Iconify icon="eva:download-fill" width={16} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Box>
    );
}

FormSubmissionCard.propTypes = {
    submission:     PropTypes.object.isRequired,
    onView:         PropTypes.func,
    onDownloadPdf:  PropTypes.func,
};

FormSubmissionCard.defaultProps = {
    onView:        null,
    onDownloadPdf: null,
};
